import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { CheckpointMeta, GameVersion, SessionAdoptRequest } from "@redchain/contracts";

export interface RomRecord {
  sha1: string;
  version: GameVersion;
  bytes: Uint8Array;
  addedAt: number;
}

export interface PendingChunk {
  sessionId: string;
  seq: number;
  /** zstd-compressed PKIL chunk, as uploaded. */
  bytes: Uint8Array;
  chainHash: string;
  firstFrame: string;
  lastFrame: string;
  createdAt: number;
}

export interface PendingCheckpoint {
  sessionId: string;
  seqInSession: number;
  meta: CheckpointMeta;
  /** zstd-compressed SRAM, as uploaded. */
  sramZst: Uint8Array;
  /** Raw SRAM, kept so a verified upload can become `last_canonical_sram`. */
  sram: Uint8Array;
  createdAt: number;
}

export interface CanonicalSram {
  player: string;
  sram: Uint8Array;
  sha256: string;
  savedAt: number;
  source: "session_start" | "committed";
  /** save_seq the SRAM was composed from, when the server said (session starts only). */
  saveSeq?: string;
}

export interface SessionRecord {
  sessionId: string;
  player: string;
  startedAt: number;
  offline: boolean;
  superseded: boolean;
  /** Offline sessions: what POST /sessions/adopt needs, recorded at boot (§7.5). */
  adopt?: SessionAdoptRequest;
  /** Offline sessions: the server adopted it, so its pending data uploads like any session's. */
  adopted?: boolean;
}

interface RedchainDb extends DBSchema {
  roms: { key: string; value: RomRecord; indexes: { by_version: GameVersion } };
  pending_chunks: { key: [string, number]; value: PendingChunk; indexes: { by_session: string } };
  pending_checkpoints: { key: [string, number]; value: PendingCheckpoint; indexes: { by_session: string } };
  last_canonical_sram: { key: string; value: CanonicalSram };
  sessions: { key: string; value: SessionRecord };
}

let dbPromise: Promise<IDBPDatabase<RedchainDb>> | null = null;

export function db(): Promise<IDBPDatabase<RedchainDb>> {
  dbPromise ??= openDB<RedchainDb>("redchain", 1, {
    upgrade(d) {
      const roms = d.createObjectStore("roms", { keyPath: "sha1" });
      roms.createIndex("by_version", "version");
      const chunks = d.createObjectStore("pending_chunks", { keyPath: ["sessionId", "seq"] });
      chunks.createIndex("by_session", "sessionId");
      const cps = d.createObjectStore("pending_checkpoints", { keyPath: ["sessionId", "seqInSession"] });
      cps.createIndex("by_session", "sessionId");
      d.createObjectStore("last_canonical_sram", { keyPath: "player" });
      d.createObjectStore("sessions", { keyPath: "sessionId" });
    },
  });
  return dbPromise;
}

// ---- roms

export async function putRom(rec: RomRecord): Promise<void> {
  await (await db()).put("roms", rec);
}

export async function romForVersion(version: GameVersion): Promise<RomRecord | undefined> {
  return (await db()).getFromIndex("roms", "by_version", version);
}

export async function allRoms(): Promise<RomRecord[]> {
  return (await db()).getAll("roms");
}

// ---- pending uploads (write-before-upload, §7.5)

export async function putPendingChunk(c: PendingChunk): Promise<void> {
  await (await db()).put("pending_chunks", c);
}

export async function putPendingCheckpoint(c: PendingCheckpoint): Promise<void> {
  await (await db()).put("pending_checkpoints", c);
}

export async function deletePendingChunk(sessionId: string, seq: number): Promise<void> {
  await (await db()).delete("pending_chunks", [sessionId, seq]);
}

export async function deletePendingCheckpoint(sessionId: string, seqInSession: number): Promise<void> {
  await (await db()).delete("pending_checkpoints", [sessionId, seqInSession]);
}

export async function pendingForSession(sessionId: string): Promise<{ chunks: PendingChunk[]; checkpoints: PendingCheckpoint[] }> {
  const d = await db();
  const [chunks, checkpoints] = await Promise.all([
    d.getAllFromIndex("pending_chunks", "by_session", sessionId),
    d.getAllFromIndex("pending_checkpoints", "by_session", sessionId),
  ]);
  chunks.sort((a, b) => a.seq - b.seq);
  checkpoints.sort((a, b) => a.seqInSession - b.seqInSession);
  return { chunks, checkpoints };
}

/** Session ids that still have pending data, oldest first. */
export async function sessionsWithPending(): Promise<string[]> {
  const d = await db();
  const ids = new Map<string, number>();
  for (const c of await d.getAll("pending_chunks")) ids.set(c.sessionId, Math.min(ids.get(c.sessionId) ?? Infinity, c.createdAt));
  for (const c of await d.getAll("pending_checkpoints")) ids.set(c.sessionId, Math.min(ids.get(c.sessionId) ?? Infinity, c.createdAt));
  return [...ids.entries()].sort((a, b) => a[1] - b[1]).map(([id]) => id);
}

export async function dropSessionPending(sessionId: string): Promise<void> {
  const d = await db();
  const tx = d.transaction(["pending_chunks", "pending_checkpoints"], "readwrite");
  for (const key of await tx.objectStore("pending_chunks").index("by_session").getAllKeys(sessionId)) await tx.objectStore("pending_chunks").delete(key);
  for (const key of await tx.objectStore("pending_checkpoints").index("by_session").getAllKeys(sessionId)) await tx.objectStore("pending_checkpoints").delete(key);
  await tx.done;
}

// ---- sessions and canonical SRAM

export async function putSession(s: SessionRecord): Promise<void> {
  await (await db()).put("sessions", s);
}

export async function getSession(sessionId: string): Promise<SessionRecord | undefined> {
  return (await db()).get("sessions", sessionId);
}

export async function putCanonicalSram(rec: CanonicalSram): Promise<void> {
  await (await db()).put("last_canonical_sram", rec);
}

export async function getCanonicalSram(player: string): Promise<CanonicalSram | undefined> {
  return (await db()).get("last_canonical_sram", player);
}
